import Modal from "@/common/elements/Modal";
import { forwardRef, useEffect, useState } from "react";
import InmateMovementDetails from "./InmateMovementDetails";
import {
  generateCurrentLocation,
  hasCheckSecurity,
  isNonEmptyVariable,
  getRefListNameByID,
} from "@/util/common_function";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { setBookings } from "@/slices/bookingsSlice";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import _ from "lodash";
import useDialog from "@/hooks/useDialog";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const HouseAndTracksForm = forwardRef(
  (
    {
      showDetailsPopup,
      setShowDetailsPopup,
      handleSave,
      selectedRow,
      houseFormRef,
      mode,
      isEdit,
      setIsEdit,
      isLoading = false,
      isScheduled = false,
      isReturn = false,
      setScheduled,
      setIsReturn,
      lastCell,
      lastLoan,
      setLastCell,
      setLastLoan,
      setSelectedRow,
      isInLoan,
      handleAddClick,
      from = "",
      startingPoint="",
      cancelTransportDefaults = null,
      setCancelTransportDefaults,
      isCancelTransport = false,
    },
    ref
  ) => {
    const { showDialog } = useDialog();
    const [isDisabled, setIsDisabled] = useState(false);
    const dispatch = useDispatch();
    const { userDetail } = useSelector((s) => s?.userDetail);
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );
    const [showPopUp, setShowPopUp] = useState(false);
    const [showPopUp2, setShowPopUp2] = useState(false);
    const [newFormDisable, setnewFormDisable] = useState(false);
    const [hitTime, setHitTime] = useState(0);
    const fullSearch = useSelector(state => state.fullSearch);

    useEffect(() => {
      if (cancelTransportDefaults) {
        // Pre-fill the form with cancel transport defaults
        setSelectedRow(cancelTransportDefaults);
        setnewFormDisable(false);
      } else if (isReturn) {
        setnewFormDisable(true);
        if (isNonEmptyVariable(lastLoan)) setSelectedRow(lastLoan);
      } else {
        setnewFormDisable(false);
      }
    }, [showDetailsPopup]);

    useEffect(() => {
      if (isEdit) {
        let isShow = hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.MODIFY,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report
        );
        if (isShow) {
          setIsDisabled(false);
        } else {
          setIsDisabled(true);
        }
      } else {
        setIsDisabled(false);
      }
    }, [isEdit]);

    function buildHousingAssignmentForInJail(houseRef) {
      const { inJailType, block, cell, bed, holdingCell } = houseRef;
      if (inJailType === "Cell") {
        return {
          housingAssignmentType: "Cell",
          housingAssignmentBlock: block || null,
          housingAssignmentCell: cell || null,
          housingAssignmentBed: bed || null,
          housingAssignmentIsolation: null,
        };
      }
      if (inJailType === "Holding") {
        return {
          housingAssignmentType: "Holding",
          housingAssignmentBlock: null,
          housingAssignmentCell: null,
          housingAssignmentBed: null,
          housingAssignmentIsolation: holdingCell || null,
        };
      }
      return {};
    }

    function getNullHousingAssignment() {
      return {
        housingAssignmentType: null,
        housingAssignmentBlock: null,
        housingAssignmentCell: null,
        housingAssignmentBed: null,
        housingAssignmentIsolation: null,
      };
    }

    function shouldClearHousingAssignment(movementStatus) {
      const existingAR = bookings?.arrestReports?.[0];
      const hasExistingHousing = !!(
        existingAR?.housingAssignmentType ||
        existingAR?.housingAssignmentBlock ||
        existingAR?.housingAssignmentCell ||
        existingAR?.housingAssignmentBed ||
        existingAR?.housingAssignmentIsolation
      );
      const prevCompleteInJail = (bookings?.inmateTracking || []).some(
        (t) => t?.inmateLocation === "In Jail" && t?.movementStatus === "Complete"
      );

      if (hasExistingHousing && prevCompleteInJail) {
        // Scenario 3: clear only when Complete
        return movementStatus === "Complete";
      }
      // Scenario 2: clear when NOT Complete
      return movementStatus !== "Complete";
    }

    function handleSaveForm() {
      setCancelTransportDefaults?.(null);
      const agency = userDetail?.staff?.organization?.[0]?.agency;
      let houseRef = houseFormRef?.current?.getFormValues();
      if (isNonEmptyVariable(houseRef)) {
        let { inmateLocation, movementStatus, transportDestination } = houseRef;
        if (isNonEmptyVariable(inmateLocation)) {
          if (inmateLocation == "In Jail" && movementStatus == "Complete" && !fullSearch) {
            let involvements = {
              invCreatedBy: userDetail?.staffId,
              invObjType: 3,
              invObjId: bookings?.bookingId,
              invCreatedDate: dayjs().format("MM/DD/YYYY HH:mm:ss"),
              linkedObjectType: "Folders",
              relatedObjectType: "Bookings",
              flagTypeID: 50030004,
              invExpireDate: null,
              invDesc: "",
            };

            // Scenario 1: update housing assignment fields from the In Jail form values
            const housingAssignment = buildHousingAssignmentForInJail(houseRef);

            dispatch(
              setBookings({
                arrestReports: [
                  {
                    ...bookings?.arrestReports?.[0],
                    currentLocation: generateCurrentLocation(
                      houseRef,
                      agency
                    ),
                    ...housingAssignment,
                  },
                ],
                cdInvolvements: [
                  ...(bookings?.cdInvolvements || []),
                  involvements,
                ],
              })
            );
          }
          if (
            inmateLocation != "In Jail" &&
            inmateLocation != "Loan" &&
            movementStatus == "Complete"
          ) {
            if (window.location.pathname === "/inmate-movement") {
              showDialog("info", {
                title: "Info",
                content:
                  "You can only create or complete transport movements from the bookings module.",
                confirmButtonText: "OK",
              });
              return;
            }
            let ar = [];
            if (
              isNonEmptyVariable(bookings?.arrestReports) &&
              bookings?.arrestReports?.length
            ) {
              ar = _.cloneDeep(bookings?.arrestReports);
              let curr = `${inmateLocation} to ${transportDestination || ""}`;
              ar[0].currentLocation = curr;
              // Scenarios 2 & 3: Perm Housing Change / Transport + Complete → clear housing
              Object.assign(ar[0], getNullHousingAssignment());
              dispatch(
                setBookings({
                  arrestReports: [...(ar || [])],
                })
              );
            }
          }
          if (
            inmateLocation != "In Jail" &&
            inmateLocation != "Loan" &&
            movementStatus == "Complete" &&
            isInLoan
          ) {
            if (window.location.pathname === "/inmate-movement") {
              showDialog("info", {
                title: "Info",
                content:
                  "You can only create or complete transport movements from the bookings module.",
                confirmButtonText: "OK",
              });
              return;
            }
            setShowPopUp2(true);
            return;
          }

          if (inmateLocation == "Loan" && movementStatus == "Complete") {
            dispatch(
              setBookings({
                arrestReports: [
                  {
                    ...bookings?.arrestReports?.[0],
                    currentLocation: !fullSearch ? generateCurrentLocation(houseRef, agency) : null,
                    // Scenarios 2 & 3: Loan + Complete → clear housing
                    ...getNullHousingAssignment(),
                  },
                ],
              })
            );
          }

          // Scenarios 2 & 3: Loan / Perm Housing Change / Transport + NOT Complete
          const isLoanPermTransport =
            inmateLocation === "Loan" ||
            inmateLocation === "Perm Housing Change" ||
            inmateLocation === "Transport";
          if (isLoanPermTransport && movementStatus !== "Complete") {
            if (shouldClearHousingAssignment(movementStatus)) {
              dispatch(
                setBookings({
                  arrestReports: [
                    {
                      ...bookings?.arrestReports?.[0],
                      ...getNullHousingAssignment(),
                    },
                  ],
                })
              );
            }
          }
        }
      }
      // setnewFormDisable(true);
      handleSave();
    }

    function handleNewEntry() {
      let newCell = {};

      newCell.bed = null;
      newCell.block = null;
      newCell.cell = null;
      newCell.moveReason = null;
      newCell.approvingOfficerStaffID = null;
      newCell.transportingOfficerStaffID = null;
      newCell.inmateLocation = "In Jail";
      newCell.movementStatus = "Complete";
      newCell.actualDateTime = dayjs();
      newCell.moveReason = "Routine Move";
      newCell.comments = "RETURNING INMATE FROM LOAN TO CELL.";

      setSelectedRow(newCell);
      setnewFormDisable(false);
      setIsReturn(() => false);
      setShowPopUp(() => false);
      setTimeout(() => {
        setShowDetailsPopup(true);
      }, 500);
    }

    function handleReturn() {
      let values = { ...lastCell };

      if (isNonEmptyVariable(values)) {

        const { inmateTrackingID, ...filteredValues } = values;

        const now = dayjs();
        const data = {
          ...filteredValues,
          expectedBackDate: isNonEmptyVariable(filteredValues?.expectedBackDateTime)
            ? dayjs(filteredValues?.expectedBackDateTime)?.format("MM/DD/YYYY")
            : null,
          expectedBackTime: isNonEmptyVariable(filteredValues?.expectedBackDateTime)
            ? dayjs(filteredValues?.expectedBackDateTime)?.format("HH:mm")
            : null,
          scheduledDate: isNonEmptyVariable(filteredValues?.scheduleDateTime)
            ? dayjs(filteredValues?.scheduleDateTime)?.format("MM/DD/YYYY")
            : null,
          scheduledTime: isNonEmptyVariable(filteredValues?.scheduleDateTime)
            ? dayjs(filteredValues?.scheduleDateTime)?.format("HH:mm")
            : null,
          // Mirrors ASP: a Return happens now, not whenever lastCell's own
          // record was originally completed. Previously this checked
          // filteredValues?.actualDateTime, a combined field that only
          // exists on InmateMovementDetails.jsx's live form state - lastCell
          // (a raw API-shaped inmateTracking entry) only ever has split
          // actualDate/actualTime, so that check was always false and these
          // silently saved as null, dropping the "at <date>" clause from the
          // Summary text entirely (confirmed via live test 2026-10-09).
          actualDate: now.format("MM/DD/YYYY"),
          actualTime: now.format("HH:mm"),
          // ASP's Original_Current_Location: what the booking's current
          // location was right before this return record was created (e.g.
          // "Loan to Job Search (Activity Request)") - feeds the Summary
          // text's "Move from X to Y" clause. Previously never set at all,
          // so the "from <previous location>" portion of the Summary was
          // always missing for Return records.
          originalCurrentLocation: bookings?.arrestReports?.[0]?.currentLocation || null,
          comments: "RETURNING INMATE FROM LOAN TO CELL.",
        };

        dispatch(
          setBookings({
            inmateTracking: [...(bookings?.inmateTracking || []), data],
          })
        );

        // Mirrors handleSaveForm()'s "In Jail + Complete" Scenario 1:
        // returning the inmate should update Current Location/housing
        // assignment back to the cell they're returning to. handleReturn()
        // is a separate quick-confirm shortcut (the "Are you moving inmate
        // back to cell X?" -> Yes popup) that bypasses handleSaveForm()
        // entirely, so without this the booking's currentLocation stayed on
        // the old Loan text indefinitely after a successful return.
        const agency = userDetail?.staff?.organization?.[0]?.agency;
        const housingAssignment = buildHousingAssignmentForInJail(values);
        dispatch(
          setBookings({
            arrestReports: [
              {
                ...bookings?.arrestReports?.[0],
                currentLocation: generateCurrentLocation(values, agency),
                ...housingAssignment,
              },
            ],
          })
        );
      }

      setIsReturn(false);
      setShowPopUp(false);
    }

    function handleReturn2() {
      setShowPopUp2(false);
      setShowDetailsPopup(false);
      setTimeout(() => {
        setIsReturn(true);
        handleAddClick();
      }, 100);
    }

    function handleSaveData() {
      // Validate FIRST, before anything below runs. handleSaveForm() (via its
      // Scenario 1/2/3 blocks) dispatches Redux updates to arrestReports'
      // currentLocation/housingAssignment and pushes a new cdInvolvements flag
      // - unconditionally, every time it runs. Those dispatches used to fire
      // before the real handleSave()/handleSaveHouseTrack() callback even
      // called validateFields(), so an invalid Save (e.g. In Jail with no
      // Jail Type selected) still silently mutated the booking's Redux state
      // on every click, even though the InmateTracking record itself was
      // never persisted (validation kept rejecting). Gating the entire body
      // behind validateFields() here mirrors ASP, where validation runs
      // before any state change, on every click, with no exceptions.
      houseFormRef?.current
        ?.validateFields()
        .then(() => {
          const houseRef = houseFormRef?.current?.getFormValues();

          // Mirrors ASP's ValidateInmateMovement.vbs gate on the conflict check
          // exactly (lines ~645-650): runs for ANY Move To except Transport, for
          // ANY status except Cancelled, and only when this save isn't itself
          // recording a Return. Previously Imats only ran this for Loan/Perm
          // Housing Change while Pending/Complete - missing In Jail, Trustee,
          // Work Release, and In Transit movements entirely (confirmed gap,
          // 2026-10-01).
          const isTransportMove = houseRef?.inmateLocation === "Transport";
          const isCancelledMove = houseRef?.movementStatus === "Cancelled";
          const isReturnSave = isNonEmptyVariable(houseRef?.returnBackDateTime);
          const shouldCheckConflict = !isTransportMove && !isCancelledMove && !isReturnSave;

          if (shouldCheckConflict) {

            const conflicts = findPendingLoanConflict(houseRef);

            if (conflicts.length > 0) {
              showDialog("error", {
                title: "Loan Schedule Conflicts",
                content: buildLoanConflictWarning(
                  houseRef,
                  conflicts,
                  bookings
                ),
                confirmButtonText: "OK",
              });

              return;
            }
          }

          if (isNonEmptyVariable(typeof setScheduled)) {
            setScheduled(false);
          }

          if (!isInLoan || isScheduled) {
            handleSaveForm();
            return;
          }

          setHitTime((prev) => {
            return prev + 1;
          });

          if (
            isReturn &&
            isNonEmptyVariable(lastCell) &&
            lastCell?.inmateLocation === "In Jail"
          ) {
            setSelectedRow(lastCell);
            setShowPopUp(true);
          } else if (hitTime === 0) {
            handleNewEntry();
          }

          handleSaveForm();
        })
        .catch(() => {
          // Invalid fields - antd already renders the inline errors on the
          // offending fields. Nothing else to do; no state is touched.
        });
    }

    function toDayjs(dateTime, date, time) {
      let result = null;
      if (dateTime) {
        result = dayjs(dateTime);
      } else if (date) {
        result = dayjs(`${date} ${time || "00:00"}`, "MM/DD/YYYY HH:mm");
      }
      return result;
    }

    function buildLoanConflictWarning(newLoan, conflicts, bookings) {
      const inmateName = getInmateDisplayName(bookings);
      const oDT = getNewMovementOutMoment(newLoan);
      const rDT = toDayjs(newLoan?.expectedBackDateTime);

      const outDate = oDT?.format("MM/DD/YYYY") || "";
      const outTime = oDT?.format("HH:mm") || "";
      const backDate = rDT?.format("MM/DD/YYYY") || "";
      const backTime = rDT?.format("HH:mm") || "";

      let message =
        `${inmateName} has schedule conflicts.\n\n` +
        ` Inmate Movement \n` +
        `Out Date           : ${outDate}\n` +
        `Out Time           : ${outTime}\n` +
        `Expected Back Date : ${backDate}\n` +
        `Expected Back Time : ${backTime}\n\n` +
        ` Conflicts With:\n`;

      conflicts.forEach(({ loan, sequence }) => {
        const conflictOut = isNonEmptyVariable(loan?.actualDate)
          ? toDayjs(null, loan.actualDate, loan.actualTime)
          : toDayjs(null, loan?.scheduledDate, loan?.scheduledTime);
        const conflictBack = isNonEmptyVariable(loan?.expectedBackDate)
          ? toDayjs(null, loan.expectedBackDate, loan.expectedBackTime)
          : null;

        const approvedBy = loan?.approvingOfficerStaffID
          ? getRefListNameByID("", loan.approvingOfficerStaffID, true, true)
          : " ";

        const destination =
          loan?.loanDestination || loan?.court || loan?.transportDestination || loan?.loanType;

        // Mirrors ASP's conflict message exactly: Sequence number is the
        // record's real Sequence (not this array's position), and includes
        // Move Reason, which Imats' message previously omitted entirely.
        message +=
          `\nSequence #${sequence ?? "?"}: ${loan?.inmateLocation || ""}` +
          `${destination ? ` (${destination})` : ""}\n` +
          `Reason             : ${loan?.moveReason || ""}\n` +
          `Scheduled/Actual At: ${conflictOut?.isValid() ? conflictOut.format("MM/DD/YYYY HH:mm") : ""}\n` +
          `Expected Back At   : ${conflictBack?.isValid() ? conflictBack.format("MM/DD/YYYY HH:mm") : ""}\n` +
          `Approved By        : ${approvedBy}\n`;
      });

      message += `\nYou must resolve all conflicts before saving.`;

      return message;
    }

    // The new movement's own "Out" moment - mirrors ASP's
    // ValidateInmateMovement.vbs: Actual Date/Time if set, else Scheduled.
    function getNewMovementOutMoment(newLoan) {
      return isNonEmptyVariable(newLoan?.actualDateTime)
        ? toDayjs(newLoan.actualDateTime)
        : toDayjs(newLoan?.scheduleDateTime);
    }

    // Adds ASP's literal "+2" padding (CheckInmateConflicts.sql): in T-SQL,
    // adding a plain integer to a datetime value adds that many DAYS. Applied
    // only to the EXISTING record's comparison timestamp, never to the new
    // movement's own O_DT/R_DT. DISPROVEN by live testing 2026-10-01: a
    // Pending movement [Sched 13:11, ExpBack 13:20] was confirmed by ASP to
    // conflict with a new Complete movement [Actual 13:17, ExpBack 13:30] -
    // only explainable if condition 3's "(ExpectedBack) BETWEEN [O_DT,R_DT]"
    // fires with NO padding (13:20 is directly between 13:17 and 13:30); with
    // a 2-day shift that same check falls far outside the window and would
    // have wrongly stayed silent. Treating the literal "+2" as a negligible
    // artifact of the original SQL (not a meaningful day-scale business
    // rule) and omitting it entirely.
    //
    // Mirrors ASP's CheckInmateConflicts stored procedure's three OR'd
    // conditions (SQLScripts\_Patch_SQL_02_Procs_Scripts_v39.sql ~2886-2937).
    // `existing` is one other InmateTracking record on this same booking;
    // oDT/rDT are the NEW movement's Out/Expected-Back moments.
    function existingRecordConflicts(existing, oDT, rDT) {
      // Existing records of these two types are never candidates, regardless
      // of the new movement's own type (SP's WHERE clause).
      if (existing?.inmateLocation === "Transport" || existing?.inmateLocation === "In Jail") {
        return false;
      }

      const scheduled = toDayjs(null, existing?.scheduledDate, existing?.scheduledTime);
      const actual = toDayjs(null, existing?.actualDate, existing?.actualTime);
      const expectedBack = toDayjs(null, existing?.expectedBackDate, existing?.expectedBackTime);
      const hasReturned = isNonEmptyVariable(existing?.returnDate);

      // Condition 1: existing record is Pending.
      if (existing?.movementStatus === "Pending" && scheduled?.isValid() && expectedBack?.isValid()) {
        const withinNewWindow = scheduled.isSameOrAfter(oDT) && scheduled.isSameOrBefore(rDT);
        const newWindowNested = scheduled.isSameOrBefore(oDT) && rDT.isSameOrBefore(expectedBack);
        if (withinNewWindow || newWindowNested) return true;
      }

      // Condition 2: existing record is Complete, not yet returned, and
      // wasn't itself a "Receive Inmate" move.
      if (
        existing?.movementStatus === "Complete" &&
        !hasReturned &&
        existing?.moveReason !== "Receive Inmate" &&
        actual?.isValid() &&
        expectedBack?.isValid()
      ) {
        const withinNewWindow = actual.isSameOrAfter(oDT) && actual.isSameOrBefore(rDT);
        const newWindowNested = actual.isSameOrBefore(oDT) && rDT.isSameOrBefore(expectedBack);
        if (withinNewWindow || newWindowNested) return true;
      }

      // Condition 3: catch-all - existing record is anything except
      // Cancelled, and not yet returned.
      if (existing?.movementStatus !== "Cancelled" && !hasReturned && expectedBack?.isValid()) {
        const withinNewWindow = expectedBack.isSameOrAfter(oDT) && expectedBack.isSameOrBefore(rDT);
        const newWindowNested =
          actual?.isValid() && actual.isSameOrBefore(oDT) && rDT.isSameOrBefore(expectedBack);
        if (withinNewWindow || newWindowNested) return true;
      }

      return false;
    }

    function findPendingLoanConflict(newLoan) {
      const selectedInmateTrackID = selectedRow?.inmateTrackingID;

      const oDT = getNewMovementOutMoment(newLoan);
      const rDT = toDayjs(newLoan?.expectedBackDateTime);
      if (!oDT?.isValid() || !rDT?.isValid()) {
        return [];
      }

      // Mirrors ASP: candidates are every OTHER InmateTracking record on this
      // same booking - NOT filtered to the new movement's own Inmate_Location
      // (previously Imats only compared Loan-vs-Loan/PHC-vs-Loan, missing
      // every cross-type conflict ASP would have caught, e.g. a new Perm
      // Housing Change overlapping an existing Loan).
      const candidates = (bookings?.inmateTracking || []).filter(
        (m) => m?.inmateTrackingID !== selectedInmateTrackID
      );

      const conflicts = [];
      candidates.forEach((movement) => {
        if (existingRecordConflicts(movement, oDT, rDT)) {
          // Sequence is the record's own real Sequence value (matches what's
          // shown in the Inmate Tracking grid), not this array's position.
          conflicts.push({ loan: movement, sequence: movement?.sequence });
        }
      });

      return conflicts;
    }

    function getInmateDisplayName(bookings) {
      const firstName = bookings?.firstName || bookings?.bio?.[0]?.fName || "";
      const lastName = bookings?.lastName || bookings?.bio?.[0]?.lName || "";

      const name =
        firstName || lastName
          ? `${lastName}${lastName && firstName ? ", " : ""}${firstName}`
          : "Inmate";
      return name;
    }

    return (
      <>
        {showDetailsPopup && (
          <Modal
            header={"Inmate Movement Details"}
            show={showDetailsPopup}
            size="xl"
            maskClosable={false}
            onHide={() => {
              setShowDetailsPopup(false);
              setnewFormDisable(false);
              setIsReturn(false);
              setHitTime(0);
              setCancelTransportDefaults?.(null);
            }}
            footerActions={[
              {
                key: "save",
                text: "Save",
                isLoading: isLoading,
                onClick: () => {
                  handleSaveData();
                },
                disabled: !bookingsFieldsEnabled || isDisabled,
              },
            ]}
            footerCloseAction={() => {
              setIsEdit(false);
              setIsReturn(false);
            }}
            footerCloseActionText="Close"
          >
            <InmateMovementDetails
              data={selectedRow}
              ref={houseFormRef}
              bookingsFieldsEnabled={bookingsFieldsEnabled}
              mode={mode}
              isScheduled={isScheduled}
              isInLoan={isInLoan}
              isReturn={isReturn}
              newFormDisable={newFormDisable}
              startingPoint={startingPoint}
              isCancelTransport={isCancelTransport}
            />
          </Modal>
        )}
        {showPopUp && lastCell?.inmateLocation == "In Jail" && (
          <Modal
            header={"Inmate Movement Details"}
            show={showPopUp}
            size="xl"
            onHide={() => setShowPopUp(false)}
            footerActions={[
              {
                key: "yes",
                text: "Yes",
                onClick: () => {
                  handleReturn();
                },
              },
              {
                key: "no",
                text: "No",
                onClick: () => {
                  handleNewEntry();
                },
              },
            ]}
            footerCloseAction={() => setShowPopUp(false)}
            footerCloseActionText="Close"
          >
            Are you moving the inmate back to{" "}
            {isNonEmptyVariable(lastCell?.block)
              ? `Cell:- ${[lastCell?.block, lastCell?.cell, lastCell?.bed]
                  .filter((v) => isNonEmptyVariable(v))
                  .join(" ")}?`
              : `Holding:- ${lastCell?.holdingCell || ""}?`}
          </Modal>
        )}

        {showPopUp2 && (
          <Modal
            header={"Inmate Movement Details"}
            show={showPopUp2}
            size="xl"
            onHide={() => setShowPopUp2(false)}
            footerActions={[
              {
                key: "return",
                text: "Return",
                // isLoading: isLoading,
                onClick: () => {
                  handleReturn2();
                },
                // disabled: !bookingsFieldsEnabled || isDisabled,
              },
              {
                key: "cancel",
                text: "Cancel",
                // isLoading: isLoading,
                onClick: () => {
                  () => setShowPopUp2(false);
                },
                // disabled: !bookingsFieldsEnabled || isDisabled,
              },
            ]}
            footerCloseAction={() => setShowPopUp2(false)}
            footerCloseActionText="Close"
          >
            This inmate is currently on a Loan. if you are the inmate to the
            facility, you should use the "inmate return" button Press{" "}
            <b>Return </b> to return inmate from loan
          </Modal>
        )}
      </>
    );
  }
);

export default HouseAndTracksForm;
